import { useSuspenseQuery } from "@apollo/client";
import Select, { SelectProps } from "@/shared/form/select";
import { AllTokens, Dictionaries } from "gql/graphql";
import { CustomOption } from "./option/option";
import { Suspense, useEffect } from "react";
import { GET_DICTIONARY } from "./api/get-dictionary/get-dictionary";
import { useTranslations } from "use-intl";
import Divider from "@/shared/layout/divider";
import { ToDictionaryCreateValue } from "@/modules/common/pages/dictionaries/dictionary-create-value";
import { useRefetchOnWindowActive } from "./utils/use-refetch-on-window-active";
import { Guard } from "@/shared/security/model/guard/guard";

export type DictionaryProps = SelectProps & {
  dictionary: keyof typeof Dictionaries;
  withoutDefaultValue?: boolean;
  defaultCode?: string;
  /** Что хранить значением поля: id значения (по умолчанию) или его код. */
  valueKey?: "id" | "code";

  // Если содержится два одинаковых словаря на форме, то при useSuspenseQuery.refetch может происходить подвисание одного из компонентов в Suspense.fallback,
  // если передать уникальный queryKey, то такого не будет
  queryKey?: string;
};

export default function DictionaryContainer(props: DictionaryProps) {
  return (
    <Suspense fallback={<DictionaryView loading />}>
      <Dictionary {...props} />
    </Suspense>
  );
}

function Dictionary({
  dictionary,
  withoutDefaultValue,
  defaultValue,
  placeholder,
  defaultCode,
  queryKey,
  valueKey = "id",
  ...rest
}: DictionaryProps) {
  const t = useTranslations("Dictionary");
  const { data, refetch } = useSuspenseQuery(GET_DICTIONARY, {
    variables: {
      dictionary: Dictionaries[dictionary],
    },
    fetchPolicy: "network-only",
    queryKey,
  });

  const { onChange, value } = rest;

  useRefetchOnWindowActive(refetch);

  useEffect(() => {
    if (value) {
      return;
    }

    if (defaultCode) {
      const recordByCode = data?.getDictionaryValues?.values?.find(
        (value) => value.code === defaultCode,
      );

      if (onChange && recordByCode) {
        onChange(recordByCode[valueKey], {
          label: recordByCode.value.value,
          value: recordByCode[valueKey],
        });
      }

      return;
    }

    const defaultRecord = data?.getDictionaryValues?.default;
    const defaultId = defaultRecord?.[valueKey];
    const defaultValueDictionary = defaultRecord?.value?.value;

    if (onChange && defaultId && !withoutDefaultValue) {
      onChange(defaultId, {
        label: defaultValue || defaultValueDictionary,
        value: defaultId,
      });
    }
  }, [
    data?.getDictionaryValues?.default,
    withoutDefaultValue,
    defaultValue,
    defaultCode,
    data?.getDictionaryValues?.values,
    valueKey,
  ]);

  return (
    <DictionaryView
      {...rest}
      options={(data?.getDictionaryValues?.values ?? []).map((option) => ({
        option,
        label: option.value.value,
        value: option[valueKey],
      }))}
      placeholder={
        placeholder ??
        t("PLACEHOLDER_DICTIONARY", {
          dictionaryName: t(dictionary).toLowerCase(),
        })
      }
      dropdownRender={(options) => {
        return (
          <div>
            <div className="[&_.ant-select-item-option]:overflow-hidden [&_.ant-select-item-option]:!p-0">
              {options}
            </div>

            <Guard.Hide.IfCantModify
              influencedBy={[AllTokens.AdminCompanyDictionaries]}
            >
              <Divider className="!my-2" />
              <div className="flex justify-end">
                <ToDictionaryCreateValue dictionary={Dictionaries[dictionary]}>
                  {t("CREATE_VALUE")}
                </ToDictionaryCreateValue>
              </div>
            </Guard.Hide.IfCantModify>
          </div>
        );
      }}
    />
  );
}

function DictionaryView(props: SelectProps) {
  return (
    <Select
      {...props}
      popupMatchSelectWidth={false}
      optionRender={(option) => {
        return <CustomOption option={option.data.option} />;
      }}
      allowClear
    />
  );
}
